// src/utils/deeplink.js
// 권장 UX:
//  - iOS: (가능하면 Universal Link) → 커스텀 스킴 시도 → 타임아웃 후 App Store 폴백
//  - Android: intent:// 로 실행 → 실패 시 Play 스토어(내부 테스트 URL)로 자동 폴백
//  - Desktop(mac/Windows): 모달 열어서 QR/링크 제공(또는 OS별 스토어로 분기)

import { detectEnv } from "./platform";

// ✅ 제공받은 Android 내부 테스트 URL
const ANDROID_INTERNAL_TEST_URL =
  "https://play.google.com/store/apps/details?id=com.prinmann.xinvana";

// ⚠️ iOS 미배포 상태 → 임시로 App Store 홈으로 이동(배포 후 실제 앱 ID로 교체)
const IOS_APP_STORE_URL = "https://apps.apple.com/kr/app/xinvana/id6749616450";

export const CONFIG = {
  ios: {
    universalLink: "", // 준비되어 있으면 넣으세요 (예: https://app.example.com/open/home)
    schemeUrl: "myapp://home", // TODO: 실제 앱 스킴으로 교체
    appStoreUrl: IOS_APP_STORE_URL, // 임시: App Store 홈 (배포 후 idXXXXXXXXX 로 교체)
    openTimeoutMs: 1200, // 800~1500ms 권장
  },
  android: {
    scheme: "myapp", // TODO: 실제 스킴으로 교체
    packageName: "com.example.app", // TODO: 실제 패키지명으로 교체
    playStoreUrl: ANDROID_INTERNAL_TEST_URL, // 내부 테스트 URL
  },
};

export function buildQrUrls() {
  const appStore = CONFIG.ios.appStoreUrl;
  const play = CONFIG.android.playStoreUrl;
  return {
    iosQr: `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
      appStore,
    )}`,
    androidQr: `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
      play,
    )}`,
  };
}

/**
 * 모바일: 앱 실행 시도 → 실패 시 스토어 폴백
 * 데스크톱: onDesktop 콜백으로 모달/가이드 열기
 */
export function openAppOrStoreDesktopAware({ onDesktop } = {}) {
  const env = detectEnv();

  // ───────── iOS ─────────
  if (env.isIOS) {
    const { universalLink, appStoreUrl } = CONFIG.ios;

    // Universal Link가 있으면 우선 시도(설치시 앱, 미설치시 웹)
    if (universalLink) {
      window.location.href = universalLink;
      return;
    }

    // iOS: 커스텀 스킴 시도 시 발생하는 "주소가 유효하지 않기 때문에 Safari가 해당 페이지를 열 수 없습니다." 
    // 경고 팝업을 방지하기 위해 곧바로 App Store로 이동시킵니다.
    window.location.href = appStoreUrl;

    return;
  }

  // ──────── Android ────────
  if (env.isAndroid) {
    const { scheme, packageName, playStoreUrl } = CONFIG.android;

    // intent://open#Intent;scheme=...;package=...;S.browser_fallback_url=...;end
    //  - 설치 O → 앱 열림
    //  - 설치 X → fallback URL(내부 테스트 페이지)로 자동 이동
    const intentUrl =
      `intent://open#Intent;` +
      `scheme=${scheme};` +
      `package=${packageName};` +
      `S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};` +
      `end`;

    window.location.href = intentUrl;
    return;
  }

  // ──────── Desktop(mac/Windows) ────────
  if (typeof onDesktop === "function") {
    onDesktop(env); // 모달 열기 등 UI 제어
  }
}

// 버튼 전용: 안드로이드 버튼
export function openAndroidAppOrStore({ playUrlOverride } = {}) {
  const env = detectEnv();
  const playUrl = playUrlOverride || CONFIG.android.playStoreUrl;
  if (env.isAndroid) {
    const { scheme, packageName } = CONFIG.android;
    const intentUrl =
      `intent://open#Intent;` +
      `scheme=${scheme};` +
      `package=${packageName};` +
      `S.browser_fallback_url=${encodeURIComponent(playUrl)};` +
      `end`;
    // 클릭 이벤트 안에서 호출해야 함
    window.location.href = intentUrl;
  } else {
    // 데스크톱/타 OS는 스토어 웹페이지 새 탭
    window.open(playUrl, "_blank", "noopener,noreferrer");
  }
}

// 버튼 전용: iOS 버튼
export function openIOSAppOrStore({ appStoreUrlOverride } = {}) {
  const env = detectEnv();
  const appStoreUrl = appStoreUrlOverride || CONFIG.ios.appStoreUrl;

  if (env.isIOS) {
    // iOS: 커스텀 스킴 시도 시 발생하는 "주소가 유효하지 않기 때문에 Safari가 해당 페이지를 열 수 없습니다." 
    // 경고 팝업을 방지하기 위해 곧바로 App Store로 이동시킵니다.
    window.location.href = appStoreUrl;
  } else {
    // 데스크톱/타 OS는 App Store 웹페이지 새 탭
    window.open(appStoreUrl, "_blank", "noopener,noreferrer");
  }
}
